import { store } from 'src/app/store';
import { RTKQueryTag } from 'src/common/constants';
import type { ApiResponse } from 'src/common/interfaces';
import {
  AllowedThreatHuntingContentAPITypes,
  type CreateStageRequest,
  type ThreatHuntingGuide,
  type ThreatHuntingGuideStep,
  type ThreatHuntingScenario,
  type TimelineStage,
} from 'src/common/interfaces/threat-hunting';
import { userSelfQueryTag } from 'src/common/slices';
import { apiSlice } from '../api/api-slice';
import type {
  MitreAccuracy,
  SharedLinksData,
  SiemTool,
  ThreatHuntingActiveRun,
  ThreatHuntingHypothesisOutcome,
  ThreatHuntingRun,
  ThreatHuntingRunReport,
  ThreatHuntingStats,
  ThreatHuntingStatsRate,
  ThreatHuntingTactic,
  ThreatHuntingTechnique,
} from './threat-hunting.types';

import { THREAT_HUNTING_ONBOARDING_GUIDE_SLUG } from './threat-hunting.types';

const extendedApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getActiveThreatHuntingRun: builder.query<ThreatHuntingActiveRun | undefined, void>({
      query: () => ({
        method: 'GET',
        url: '/threat-hunting/runs/active',
      }),
      providesTags: [RTKQueryTag.ThreatHuntingRun],
      transformResponse: (response: ApiResponse<ThreatHuntingActiveRun>) => response.data,
    }),
    createThreatHuntingRun: builder.mutation<ApiResponse<{ run: string }>, { scenarioId: string; siemTool?: SiemTool }>(
      {
        query: ({ scenarioId, siemTool }) => ({
          method: 'POST',
          url: '/threat-hunting/runs',
          body: { scenarioId, siemTool },
        }),
      },
    ),
    getThreatHuntingRuns: builder.query<ThreatHuntingRun[] | undefined, void>({
      query: () => ({
        method: 'GET',
        url: '/threat-hunting/stats/runs',
      }),
      transformResponse: (response: ApiResponse<ThreatHuntingRun[]>) => response.data,
      providesTags: [RTKQueryTag.ThreatHuntingRun],
    }),
    terminateThreatHuntingRun: builder.mutation<ApiResponse, void>({
      query: () => ({
        method: 'POST',
        url: '/threat-hunting/runs/terminate',
      }),
      invalidatesTags: [RTKQueryTag.ThreatHuntingRun],
    }),
    extendThreatHuntingRunVms: builder.mutation<ApiResponse, string>({
      query: (runId) => ({
        method: 'PATCH',
        url: '/threat-hunting/vms/extend',
        body: { runId },
      }),
      invalidatesTags: [RTKQueryTag.ThreatHuntingRun],
    }),
    getThreatHuntingScenarioContent: builder.query<ThreatHuntingScenario | undefined, { id: string }>({
      query: ({ id }) => ({
        method: 'GET',
        url: '/threat-hunting/content/details',
        params: { type: AllowedThreatHuntingContentAPITypes.SCENARIO, id },
      }),
      providesTags: (response, _error, { id }) => [
        { type: RTKQueryTag.ThreatHuntingScenarioContent, id: response?._id ?? id },
      ],
      transformResponse: (response: ApiResponse<ThreatHuntingScenario[]>) => response.data?.[0],
    }),
    getThreatHuntingScenarios: builder.query<ThreatHuntingScenario[] | undefined, void>({
      query: () => ({
        method: 'GET',
        url: '/threat-hunting/content/scenarios',
      }),
      providesTags: [RTKQueryTag.ThreatHuntingScenarios],
      transformResponse: (response: ApiResponse<ThreatHuntingScenario[]>) => response.data,
    }),
    getThreatHuntingTactics: builder.query<ThreatHuntingTactic[] | undefined, void>({
      query: () => ({
        method: 'GET',
        url: '/threat-hunting/content/tactics',
      }),
      providesTags: [RTKQueryTag.ThreatHuntingTactics],
      transformResponse: (response: ApiResponse<{ id: string; name: string }[]>) => {
        if (Array.isArray(response.data) && response.data[0] && typeof response.data[0] === 'object') {
          // Use id as label and name as value
          return response.data.map((tactic: { id: string; name: string }) => ({
            label: tactic.name,
            value: tactic.id,
          }));
        }
        return response.data as ThreatHuntingTactic[] | undefined;
      },
    }),
    getThreatHuntingTechniques: builder.query<ThreatHuntingTechnique[] | undefined, { tacticName?: string } | void>({
      query: (args) => ({
        method: 'GET',
        url: '/threat-hunting/content/techniques',
        params: args && 'tacticName' in args && args.tacticName ? { tactic: args.tacticName } : undefined,
      }),
      providesTags: [RTKQueryTag.ThreatHuntingTechniques],
      transformResponse: (response: ApiResponse<{ id: string; name: string }[]>) => {
        if (Array.isArray(response.data) && response.data[0] && typeof response.data[0] === 'object') {
          // Use id as label and name as value
          return response.data.map((technique: { id: string; name: string }) => ({
            label: technique.name,
            value: technique.id,
          }));
        }
        return response.data as ThreatHuntingTechnique[] | undefined;
      },
    }),
    createTimelineStage: builder.mutation<
      ApiResponse<{ stageId: string; isToastWorthy: boolean; currentStreak: number; isStreakIncreased: boolean }>,
      CreateStageRequest
    >({
      query: (body) => ({
        method: 'POST',
        url: '/threat-hunting/timeline/stage',
        body,
      }),
      invalidatesTags: [RTKQueryTag.ThreatHuntingTimeline, RTKQueryTag.ThreatHuntingRun, userSelfQueryTag],
    }),
    getTimelineStages: builder.query<TimelineStage[] | undefined, string>({
      query: (runId) => ({
        method: 'GET',
        url: '/threat-hunting/timeline',
        params: { runId },
      }),
      providesTags: [RTKQueryTag.ThreatHuntingTimeline],
      transformResponse: (response: ApiResponse<TimelineStage[]>) => response.data,
    }),
    deleteTimelineStage: builder.mutation<ApiResponse, { id: string | undefined }>({
      query: (body) => ({
        method: 'DELETE',
        url: '/threat-hunting/timeline/stage',
        body,
      }),
      invalidatesTags: [RTKQueryTag.ThreatHuntingTimeline, RTKQueryTag.ThreatHuntingRun],
    }),
    updateTimelineStage: builder.mutation<ApiResponse<{ isToastWorthy: boolean }>, CreateStageRequest & { id: string }>(
      {
        query: (body) => ({
          method: 'PATCH',
          url: '/threat-hunting/timeline/stage',
          body,
        }),
        invalidatesTags: [RTKQueryTag.ThreatHuntingTimeline, RTKQueryTag.ThreatHuntingRun],
      },
    ),
    getThreatHuntingScenarioOnboardingSteps: builder.query<ThreatHuntingGuideStep[] | undefined, void>({
      query: () => ({
        method: 'GET',
        url: '/threat-hunting/content',
        params: { type: AllowedThreatHuntingContentAPITypes.GUIDE },
      }),
      providesTags: [RTKQueryTag.ThreatHuntingGuides],
      transformResponse: (response: ApiResponse<ThreatHuntingGuide[]>) =>
        (response.data ?? []).find((guide) => guide.slug === THREAT_HUNTING_ONBOARDING_GUIDE_SLUG)?.steps ?? [],
    }),
    startThreatHuntingInvestigation: builder.mutation<ApiResponse, void>({
      query: () => ({
        method: 'POST',
        url: '/threat-hunting/runs/start',
      }),
      invalidatesTags: [RTKQueryTag.ThreatHuntingRun],
    }),
    updateThreatHuntingHypothesisOutcome: builder.mutation<
      ApiResponse,
      { hypothesisOutcome: ThreatHuntingHypothesisOutcome }
    >({
      query: (body) => ({
        method: 'POST',
        url: '/threat-hunting/runs/hypothesis-outcome',
        body,
      }),
      invalidatesTags: [RTKQueryTag.ThreatHuntingRun],
    }),
    generateThreatReport: builder.mutation<ApiResponse, void>({
      query: () => ({
        method: 'POST',
        url: '/threat-hunting/runs/threat-report',
      }),
      invalidatesTags: [RTKQueryTag.ThreatHuntingRun],
    }),
    getRunSummaryPageData: builder.query<ThreatHuntingRunReport | undefined, { runId: string }>({
      query: ({ runId }) => ({
        method: 'GET',
        url: '/threat-hunting/runs/summary',
        params: { runId },
      }),
      transformResponse: (response: ApiResponse<ThreatHuntingRunReport>) => response.data,
    }),
    submitThreatHuntingFindings: builder.mutation<ApiResponse, void>({
      query: () => ({
        method: 'POST',
        url: '/threat-hunting/runs/submit-findings',
      }),
      invalidatesTags: [RTKQueryTag.ThreatHuntingRun],
    }),
    refreshThreatHuntingRunVms: builder.mutation<ApiResponse<{ refreshed: boolean }>, string>({
      query: (runId) => ({
        method: 'POST',
        url: '/threat-hunting/vms/refresh-url',
        body: { runId },
      }),
    }),
    pauseThreatHuntingRun: builder.mutation<ApiResponse, void>({
      query: () => ({
        method: 'POST',
        url: '/threat-hunting/runs/pause',
      }),
      invalidatesTags: [RTKQueryTag.ThreatHuntingRun, RTKQueryTag.ThreatHuntingScenarios],
    }),
    resumeThreatHuntingRun: builder.mutation<ApiResponse<{ run: string }>, { scenarioId: string }>({
      query: ({ scenarioId }) => ({
        method: 'POST',
        url: '/threat-hunting/runs/resume',
        body: { scenarioId },
      }),
      invalidatesTags: [RTKQueryTag.ThreatHuntingScenarios],
    }),
    reorderTimelineStages: builder.mutation<ApiResponse, { stages: { id: string; order: number }[] }>({
      query: ({ stages }) => ({
        method: 'POST',
        url: '/threat-hunting/timeline/stage/reorder',
        body: { stages },
      }),
      invalidatesTags: [RTKQueryTag.ThreatHuntingTimeline, RTKQueryTag.ThreatHuntingRun],
    }),
    getThreatHuntingScenarioSuccessRate: builder.query<ThreatHuntingStatsRate, { startDate: string; endDate: string }>({
      query: ({ startDate, endDate }) => ({
        method: 'GET',
        url: '/threat-hunting/stats/scenario-success-rate',
        params: { startDate, endDate },
      }),
      transformResponse: (response: ApiResponse<ThreatHuntingStatsRate>) => response.data ?? [],
    }),
    getMitreAccuracy: builder.query<MitreAccuracy | undefined, void>({
      query: () => ({
        method: 'GET',
        url: '/threat-hunting/stats/mitre',
      }),
      providesTags: [RTKQueryTag.ThreatHuntingMitreAccuracy],
      transformResponse: (response: ApiResponse<MitreAccuracy>) => response.data,
    }),
    getThreatHuntingTacticAndTechniqueAccuracyRate: builder.query<
      ThreatHuntingStatsRate,
      { startDate: string; endDate: string; type: 'tactic' | 'technique'; value?: string }
    >({
      query: ({ startDate, endDate, type, value }) => ({
        method: 'GET',
        url: '/threat-hunting/stats/tactic-and-technique-accuracy-rate',
        params: { startDate, endDate, type, value },
      }),
      transformResponse: (response: ApiResponse<ThreatHuntingStatsRate>) => response.data ?? [],
    }),
    getThreatHuntingStats: builder.query<ThreatHuntingStats | undefined, void>({
      query: () => ({
        method: 'GET',
        url: '/threat-hunting/stats',
      }),
      transformResponse: (response: ApiResponse<ThreatHuntingStats>) => response.data,
    }),
    getThreatHuntingOverallEvaluation: builder.query<string | undefined, { runId: string }>({
      query: ({ runId }) => ({
        method: 'GET',
        url: '/threat-hunting/runs/summary/overall-evaluation',
        params: { runId },
      }),
      providesTags: [RTKQueryTag.ThreatHuntingOverallEvaluation],
      transformResponse: (response: ApiResponse<string | undefined>) => response.data,
    }),
    getPublicThreatHuntingRunSummary: builder.query<ThreatHuntingRunReport | undefined, { runId: string }>({
      query: ({ runId }) => ({
        method: 'GET',
        url: `/threat-hunting/public/runs/summary?runId=${runId}`,
      }),
      transformResponse: (response: ApiResponse<ThreatHuntingRunReport>) => response.data,
    }),
    getPublicThreatHuntingOverallEvaluation: builder.query<string | undefined, { runId: string }>({
      query: ({ runId }) => ({
        method: 'GET',
        url: `/threat-hunting/public/runs/summary/overall-evaluation?runId=${runId}`,
      }),
      transformResponse: (response: ApiResponse<{ overallEvaluation: string }>) =>
        response.data?.overallEvaluation ?? undefined,
    }),
    getThreatHuntingSharedLinks: builder.query<SharedLinksData | undefined, { runId: string }>({
      query: ({ runId }) => ({
        method: 'GET',
        url: `/threat-hunting/runs/shared-links?runId=${runId}`,
      }),
      providesTags: (_, __, { runId }) => [{ type: RTKQueryTag.ThreatHuntingRunSummarySharedLinks, id: runId }],
      transformResponse: (response: ApiResponse<SharedLinksData>) => response.data,
    }),
    updateThreatHuntingNotes: builder.mutation<ApiResponse, { notes: string }>({
      query: ({ notes }) => ({
        method: 'PATCH',
        url: '/threat-hunting/runs/notes',
        body: { notes },
      }),
      invalidatesTags: [RTKQueryTag.ThreatHuntingRun],
    }),
  }),
});

export const invalidateActiveRunQuery = () =>
  store.dispatch(extendedApiSlice.util.invalidateTags([RTKQueryTag.ThreatHuntingRun]));

export const {
  useGetActiveThreatHuntingRunQuery,
  useCreateThreatHuntingRunMutation,
  useTerminateThreatHuntingRunMutation,
  useExtendThreatHuntingRunVmsMutation,
  useGetThreatHuntingScenarioContentQuery,
  useGetThreatHuntingScenariosQuery,
  useGetThreatHuntingTacticsQuery,
  useGetThreatHuntingTechniquesQuery,
  useCreateTimelineStageMutation,
  useGetTimelineStagesQuery,
  useDeleteTimelineStageMutation,
  useUpdateTimelineStageMutation,
  useGetThreatHuntingScenarioOnboardingStepsQuery,
  useStartThreatHuntingInvestigationMutation,
  useUpdateThreatHuntingHypothesisOutcomeMutation,
  useGenerateThreatReportMutation,
  useGetRunSummaryPageDataQuery,
  useSubmitThreatHuntingFindingsMutation,
  useRefreshThreatHuntingRunVmsMutation,
  usePauseThreatHuntingRunMutation,
  useResumeThreatHuntingRunMutation,
  useReorderTimelineStagesMutation,
  useGetThreatHuntingScenarioSuccessRateQuery,
  useGetThreatHuntingTacticAndTechniqueAccuracyRateQuery,
  useGetMitreAccuracyQuery,
  useGetThreatHuntingStatsQuery,
  useGetThreatHuntingRunsQuery,
  useGetThreatHuntingOverallEvaluationQuery,
  useGetPublicThreatHuntingRunSummaryQuery,
  useGetPublicThreatHuntingOverallEvaluationQuery,
  useGetThreatHuntingSharedLinksQuery,
  useUpdateThreatHuntingNotesMutation,
} = extendedApiSlice;
